import React, { Suspense, useEffect } from 'react'
import { Switch, Route, NotFound } from '@fs/zion-router'
import { colors } from '@fs/zion-ui'
import ErrorBoundary from '@fs/zion-error-boundary'
import Head, { PageHead } from '@fs/zion-head'
import { GroupsProvider } from '@fs/zion-tree-groups'
import { useTranslation } from 'react-i18next'
import { useIsHelping } from '@fs/zion-user'
import { FeedBadgeProvider } from './providers/FeedBadgeProvider'
import { usePWAMode, useShowTWAUpgradeWarning, useServiceWorkerUpdater } from './hooks'
import getIsTWAMode from './helpers/appMode_helper'
import UpdateTWANotice from './pages/UpdateTWANotice/UpdateTWANotice'
import useMigration from './hooks/useMigration'
import useAppRoutes from './hooks/useAppRoutes'
import useTWAVisibilityHandler from './hooks/useTWAVisibilityHandler'

const HomePageSkeleton = () => null

function App() {
  const { t } = useTranslation()
  const isHelping = useIsHelping()
  const isPWAMode = usePWAMode()
  const isTWAMode = getIsTWAMode()

  useTWAVisibilityHandler({ isTWAMode })
  useServiceWorkerUpdater()
  useMigration()

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.has('closeTogetherPage')) {
      if (isPWAMode && !isTWAMode) {
        window.close()
      }
    }
  }, [isPWAMode, isTWAMode])

  const MINIMUM_TWA_VERSION = 66
  const showTWAUpgrade = useShowTWAUpgradeWarning(isTWAMode, MINIMUM_TWA_VERSION)

  const { routeElements } = useAppRoutes({
    isHelping,
  })

  if (showTWAUpgrade) {
    return <UpdateTWANotice />
  }

  return (
    <ErrorBoundary>
      <GroupsProvider>
        <FeedBadgeProvider>
          <Suspense fallback={<HomePageSkeleton />}>
            <Head>
              <meta name="apple-itunes-app" content="app-id=1626322239" />
              <meta name="robots" content="noindex" />
              <meta name="theme-color" content={colors.gray00} />
              <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
              <link rel="apple-touch-startup-image" href="./logo512.png" />
            </Head>
            <PageHead
              omitSiteName
              title={t('familyconnect.page.title', 'Together By FamilySearch: Share your family stories!')}
              description={t(
                'familyconnect.page.description',
                'Build stronger family bonds and create lasting memories by sharing stories that matter—from epic family vacations to lessons learned from your personal heroes.'
              )}
            />
            <Switch>
              {routeElements}
              <Route component={NotFound} />
            </Switch>
          </Suspense>
        </FeedBadgeProvider>
      </GroupsProvider>
    </ErrorBoundary>
  )
}
export default App
